/**
 * 「议程」 page (v0.6.0): one selected local day.
 *
 * Sections: 清单 (plans for the day + undated plans, add/done/delete), 课程
 * (the day's timetable from the Learn sync) and 作业 (stored unsubmitted work).
 *
 * Data is authoritative and transport-specific:
 * - demo mode renders the clearly-labelled local demo store; add/done/delete
 *   mutate only that local store so the UI flows can be exercised offline;
 * - live mode renders only a validated daemon `plan.response` agenda and never
 *   inserts an optimistic row or reports fake success.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { StatusBanner } from '../components/StatusBanner';
import {
  AGENDA_WEEKDAY_LABELS,
  addLocalDays,
  academicWeekForLocalDate,
  agendaDateHeading,
  emptyPlanForm,
  planWhenLabel,
  todayLocalDate,
  validatePlanForm,
  weekdayOfLocalDate,
  type AgendaAssignment,
  type AgendaClass,
  type Plan,
  type PlanDraft,
  type PlanFormErrors,
  type PlanFormValues,
} from '../lib/agenda';
import { formatClock, formatRelative } from '../lib/format';
import { nowSeconds } from '../lib/id';
import { useApp } from '../state/AppState';

const INPUT_CLASS =
  'mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-indigo-400/50';

function PlanForm({
  initialDate,
  busy,
  onSave,
  onCancel,
}: {
  initialDate: string;
  busy: boolean;
  onSave(draft: PlanDraft): Promise<boolean>;
  onCancel(): void;
}) {
  const [values, setValues] = useState<PlanFormValues>(() => emptyPlanForm(initialDate));
  const [errors, setErrors] = useState<PlanFormErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  const update = <Key extends keyof PlanFormValues>(key: Key, value: PlanFormValues[Key]): void => {
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    const validation = validatePlanForm(values, initialDate);
    if (!validation.ok) {
      setErrors(validation.errors);
      setMessage(validation.message);
      return;
    }
    setErrors({});
    setMessage(null);
    const saved = await onSave(validation.value);
    if (!saved) setMessage('保存失败，请查看上方错误提示');
  };

  return (
    <div
      data-testid="agenda-plan-form"
      className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
    >
      <div className="space-y-2.5">
        <div>
          <label className="text-[11px] text-slate-400" htmlFor="agenda-plan-text">
            清单内容
          </label>
          <input
            id="agenda-plan-text"
            data-testid="agenda-plan-text"
            type="text"
            value={values.text}
            maxLength={500}
            placeholder="要做什么 / 要去哪？"
            onChange={(event) => update('text', event.target.value)}
            className={INPUT_CLASS}
          />
          {errors.text ? <p className="mt-1 text-[10px] text-rose-300">{errors.text}</p> : null}
        </div>

        <div>
          <label className="text-[11px] text-slate-400" htmlFor="agenda-plan-notes">
            备注（可选）
          </label>
          <textarea
            id="agenda-plan-notes"
            value={values.notes}
            rows={2}
            maxLength={2000}
            placeholder="可选：地点、链接、上下文"
            onChange={(event) => update('notes', event.target.value)}
            className={`${INPUT_CLASS} resize-none`}
          />
        </div>

        <label className="flex items-center gap-2 text-[11px] text-slate-300">
          <input
            type="checkbox"
            data-testid="agenda-plan-undated"
            checked={values.undated}
            onChange={(event) => update('undated', event.target.checked)}
            className="h-4 w-4 rounded border-white/20 bg-white/5 accent-indigo-500"
          />
          不设日期（一直保留，直到标记完成或删除）
        </label>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] text-slate-400" htmlFor="agenda-plan-date">
              日期（可选）
            </label>
            <input
              id="agenda-plan-date"
              data-testid="agenda-plan-date"
              type="date"
              value={values.date}
              disabled={values.undated}
              onChange={(event) => update('date', event.target.value)}
              className={`${INPUT_CLASS} disabled:opacity-40`}
            />
            {errors.date ? <p className="mt-1 text-[10px] text-rose-300">{errors.date}</p> : null}
          </div>
          <div>
            <label className="text-[11px] text-slate-400" htmlFor="agenda-plan-time">
              时间（可选）
            </label>
            <input
              id="agenda-plan-time"
              data-testid="agenda-plan-time"
              type="time"
              value={values.time}
              disabled={values.undated}
              onChange={(event) => update('time', event.target.value)}
              className={`${INPUT_CLASS} disabled:opacity-40`}
            />
            {errors.time ? <p className="mt-1 text-[10px] text-rose-300">{errors.time}</p> : null}
          </div>
        </div>

        {message ? (
          <p className="rounded-lg border border-rose-400/25 bg-rose-400/10 px-2.5 py-1.5 text-[11px] text-rose-200">
            {message}
          </p>
        ) : null}

        <div className="flex gap-2">
          <button
            type="button"
            data-testid="agenda-plan-save"
            disabled={busy}
            onClick={() => void submit()}
            className="flex-1 rounded-xl bg-indigo-500 py-2.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '保存中…' : '添加清单'}
          </button>
          <button
            type="button"
            data-testid="agenda-plan-cancel"
            disabled={busy}
            onClick={onCancel}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-slate-300 disabled:opacity-40"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanRow({
  plan,
  today,
  busy,
  readOnly,
  onDone,
  onDelete,
}: {
  plan: Plan;
  today: string;
  busy: boolean;
  readOnly: boolean;
  onDone(plan: Plan): void;
  onDelete(plan: Plan): void;
}) {
  const done = plan.status === 'done';
  return (
    <li
      data-testid="agenda-plan"
      data-id={plan.id}
      data-status={plan.status}
      className={`rounded-xl border px-3 py-2.5 ${
        done ? 'border-white/5 bg-white/[0.02]' : 'border-white/10 bg-white/[0.03]'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          data-testid="agenda-plan-done"
          aria-label={done ? '已完成' : '标记为已完成'}
          aria-pressed={done}
          disabled={busy || readOnly || done}
          onClick={() => onDone(plan)}
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition disabled:opacity-40 ${
            done
              ? 'border-emerald-400/50 bg-emerald-400/20 text-emerald-200'
              : 'border-white/20 bg-white/5 text-transparent'
          }`}
        >
          <Icon name="check" className="h-3 w-3" />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p
              className={`min-w-0 break-words text-sm leading-snug ${
                done ? 'text-slate-500 line-through' : 'text-slate-100'
              }`}
            >
              {plan.text}
            </p>
            <span className="flex shrink-0 items-center gap-1">
              {done ? (
                <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-1.5 py-0.5 text-[9px] text-emerald-200">
                  已完成
                </span>
              ) : null}
              <button
                type="button"
                data-testid="agenda-plan-delete"
                aria-label="删除清单项"
                disabled={busy || readOnly}
                onClick={() => onDelete(plan)}
                className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[10px] text-rose-100 disabled:opacity-40"
              >
                删除
              </button>
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] text-slate-400">
              {planWhenLabel(plan, today)}
            </span>
            {plan.notes ? (
              <span className="break-words text-[10px] text-slate-500">{plan.notes}</span>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

function ClassRow({ item }: { item: AgendaClass }) {
  return (
    <li
      data-testid="agenda-class"
      data-id={item.id}
      className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-sm text-slate-100">{item.course}</p>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {item.start_time}–{item.end_time} · 第 {item.start_period}
            {item.end_period !== item.start_period ? `–${item.end_period}` : ''} 节
            {item.location ? ` · ${item.location}` : ''}
          </p>
        </div>
        {item.teacher ? (
          <span className="shrink-0 text-[10px] text-slate-500">{item.teacher}</span>
        ) : null}
      </div>
    </li>
  );
}

function AssignmentRow({
  item,
  now,
  busy,
  readOnly,
  onDismiss,
}: {
  item: AgendaAssignment;
  now: number;
  busy: boolean;
  readOnly: boolean;
  onDismiss(item: AgendaAssignment): void;
}) {
  const overdue = item.due_at !== null && item.due_at <= now;
  return (
    <li
      data-testid="agenda-assignment"
      data-id={item.id}
      className={`rounded-xl border px-3 py-2 ${
        overdue ? 'border-rose-400/20 bg-rose-400/[0.04]' : 'border-white/10 bg-white/[0.03]'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-sm text-slate-100">{item.title}</p>
          <p className="mt-0.5 text-[11px] text-slate-400">{item.course}</p>
        </div>
        <button
          type="button"
          data-testid="agenda-assignment-dismiss"
          aria-label="从议程隐藏这条作业"
          disabled={busy || readOnly}
          onClick={() => onDismiss(item)}
          className="shrink-0 rounded-lg border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[10px] text-rose-100 disabled:opacity-40"
        >
          移除
        </button>
      </div>
      <p className={`mt-1 text-[10px] ${overdue ? 'text-rose-300' : 'text-slate-500'}`}>
        {item.due_at !== null
          ? `${overdue ? '已截止 · ' : '截止 '}${formatClock(item.due_at)}（${formatRelative(item.due_at, now)}）`
          : '无截止日期'}
      </p>
    </li>
  );
}

function SectionHeader({
  title,
  count,
  action,
}: {
  title: string;
  count: number;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 pb-1.5">
      <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
      <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
        {count}
      </span>
      <span className="h-px flex-1 bg-white/5" />
      {action}
    </div>
  );
}

export function AgendaPage() {
  const {
    settings,
    network,
    agenda,
    agendaAvailable,
    agendaUnavailableReason,
    agendaDate,
    agendaSyncedAt,
    agendaLoading,
    agendaError,
    refreshAgenda,
    createPlan,
    completePlan,
    deletePlan,
    dismissAssignment,
  } = useApp();

  const [date, setDate] = useState(() => todayLocalDate(nowSeconds()));
  const [formOpen, setFormOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Plan | null>(null);
  const [dismissing, setDismissing] = useState<AgendaAssignment | null>(null);
  const now = nowSeconds();
  const today = todayLocalDate(now);

  // Load on mount/date change and once the live relay is actually connected
  // (demo mode needs no relay). The demo store is local and always available.
  useEffect(() => {
    if (!agendaAvailable) return;
    if (!settings.demoMode && !network.relayConnected) return;
    void refreshAgenda(date);
  }, [agendaAvailable, settings.demoMode, network.relayConnected, date, refreshAgenda]);

  const dayAgenda = agenda !== null && agenda.date === date ? agenda : null;
  const plans = dayAgenda?.plans ?? [];
  const classes = dayAgenda?.classes ?? [];
  const assignments = dayAgenda?.assignments ?? [];
  const readOnly = !agendaAvailable;
  // Live mode waits for the relay before asking; demo mode is always local.
  const waitingRelay = !settings.demoMode && !network.relayConnected;

  const complete = async (plan: Plan): Promise<void> => {
    if (readOnly) return;
    setBusyId(plan.id);
    await completePlan(plan.id);
    setBusyId(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleting === null) return;
    setBusyId(deleting.id);
    const result = await deletePlan(deleting.id);
    setBusyId(null);
    if (result.ok) setDeleting(null);
  };

  const confirmDismiss = async (): Promise<void> => {
    if (dismissing === null) return;
    setBusyId(dismissing.id);
    const result = await dismissAssignment(dismissing.id);
    setBusyId(null);
    if (result.ok) setDismissing(null);
  };

  return (
    <div className="h-full overflow-y-auto">
      <StatusBanner />
      <div className="px-4 pb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-base font-semibold text-slate-100">议程</h1>
            <p className="text-[11px] text-slate-500">清单 · 课程 · 未提交作业（按手机本地日期）</p>
          </div>
          {settings.demoMode ? (
            <span
              data-testid="agenda-demo-badge"
              className="shrink-0 rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[10px] text-sky-200"
            >
              演示 · 本地数据
            </span>
          ) : null}
        </div>

        <div className="mt-2 flex items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
          <button
            type="button"
            data-testid="agenda-prev"
            aria-label="前一天"
            onClick={() => setDate((current) => addLocalDays(current, -1))}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-slate-300"
          >
            <Icon name="back" className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1 text-center">
            <p
              data-testid="agenda-date-heading"
              className="truncate text-sm font-medium text-slate-100"
            >
              {agendaDateHeading(date)}
              {date === today ? ' · 今天' : ''}
            </p>
            <p className="text-[10px] text-slate-500">
              {AGENDA_WEEKDAY_LABELS[weekdayOfLocalDate(date) - 1] ?? ''}
              {academicWeekForLocalDate(date) === null
                ? ' · 非本学期'
                : ` · 第 ${academicWeekForLocalDate(date)} 周`}
              {' · '}
              {settings.demoMode ? '本地演示数据' : 'daemon 议程'}
            </p>
          </div>
          <button
            type="button"
            data-testid="agenda-next"
            aria-label="后一天"
            onClick={() => setDate((current) => addLocalDays(current, 1))}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-slate-300"
          >
            <Icon name="chevron" className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="agenda-today"
            disabled={date === today}
            onClick={() => setDate(today)}
            className="rounded-lg border border-indigo-400/40 bg-indigo-400/15 px-2 py-1.5 text-[11px] text-indigo-100 disabled:opacity-40"
          >
            今天
          </button>
          <button
            type="button"
            data-testid="agenda-refresh"
            aria-label="刷新议程"
            disabled={!agendaAvailable || agendaLoading || waitingRelay}
            onClick={() => void refreshAgenda(date)}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-slate-300 disabled:opacity-40"
          >
            <Icon name="refresh" className={`h-4 w-4 ${agendaLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {settings.demoMode ? (
          <p className="mt-2 text-[10px] leading-relaxed text-slate-500">
            演示模式：清单/课程/作业均为本地模拟数据，添加与完成只改本机演示状态，不会访问 daemon。
          </p>
        ) : null}

        {!agendaAvailable ? (
          <p
            data-testid="agenda-unavailable"
            className="mt-2 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200"
          >
            {agendaUnavailableReason ?? '议程当前不可用'}
          </p>
        ) : null}

        {agendaError ? (
          <div
            data-testid="agenda-error"
            className="mt-2 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
          >
            <p className="break-words">{agendaError}</p>
            {agendaAvailable ? (
              <button
                type="button"
                onClick={() => void refreshAgenda(date)}
                disabled={agendaLoading}
                className="mt-2 rounded-lg border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
              >
                重试
              </button>
            ) : null}
          </div>
        ) : null}

        {!agendaAvailable ? null : (
          <div className="mt-3 space-y-4">
            {dayAgenda === null && agendaError !== null ? null : waitingRelay ? (
              <p
                data-testid="agenda-waiting"
                className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-4 text-center text-[11px] text-amber-200"
              >
                等待 live relay 连接后读取议程（不会显示旧数据或假数据）。
              </p>
            ) : dayAgenda === null ? (
              <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 text-center text-[11px] text-slate-500">
                正在读取议程…
              </p>
            ) : (
              <>
                <section data-testid="agenda-plans-section">
                  <SectionHeader
                    title="清单"
                    count={plans.length}
                    action={
                      <button
                        type="button"
                        data-testid="agenda-plan-new"
                        disabled={formOpen || readOnly}
                        onClick={() => setFormOpen(true)}
                        className="flex items-center gap-1 rounded-full border border-indigo-400/40 bg-indigo-400/15 px-2.5 py-1 text-[11px] text-indigo-100 disabled:opacity-40"
                      >
                        <Icon name="plus" className="h-3 w-3" />
                        新建
                      </button>
                    }
                  />
                  {formOpen ? (
                    <div className="mb-2">
                      <PlanForm
                        initialDate={date}
                        busy={agendaLoading}
                        onSave={async (draft) => {
                          const result = await createPlan(draft);
                          if (result.ok) setFormOpen(false);
                          return result.ok;
                        }}
                        onCancel={() => {
                          if (agendaLoading) return;
                          setFormOpen(false);
                        }}
                      />
                    </div>
                  ) : null}
                  {plans.length === 0 && !formOpen ? (
                    <p
                      data-testid="agenda-plans-empty"
                      className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 text-center text-[11px] text-slate-500"
                    >
                      当天没有清单项，也没有无日期清单；点“新建”记一条。
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {plans.map((plan) => (
                        <PlanRow
                          key={plan.id}
                          plan={plan}
                          today={today}
                          busy={busyId === plan.id}
                          readOnly={readOnly}
                          onDone={(item) => void complete(item)}
                          onDelete={(item) => setDeleting(item)}
                        />
                      ))}
                    </ul>
                  )}
                </section>

                <section data-testid="agenda-classes-section">
                  <SectionHeader title="课程" count={classes.length} />
                  {classes.length === 0 ? (
                    <p
                      data-testid="agenda-classes-empty"
                      className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 text-center text-[11px] text-slate-500"
                    >
                      当天没有课程（课表来自网络学堂同步）。
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {classes.map((item) => (
                        <ClassRow key={item.id} item={item} />
                      ))}
                    </ul>
                  )}
                </section>

                <section data-testid="agenda-assignments-section">
                  <SectionHeader title="作业" count={assignments.length} />
                  {assignments.length === 0 ? (
                    <p
                      data-testid="agenda-assignments-empty"
                      className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 text-center text-[11px] text-slate-500"
                    >
                      没有未提交作业。
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {assignments.map((item) => (
                        <AssignmentRow
                          key={item.id}
                          item={item}
                          now={now}
                          busy={busyId === item.id}
                          readOnly={readOnly}
                          onDismiss={(assignment) => setDismissing(assignment)}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}

            {agendaSyncedAt !== null && dayAgenda !== null ? (
              <p className="text-[10px] text-slate-500">
                上次同步 {formatClock(agendaSyncedAt)}
                {agendaDate !== null && agendaDate !== date ? ' · 日期已切换，正在刷新' : ''}
              </p>
            ) : null}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleting !== null}
        title="删除这条清单？"
        danger
        busy={agendaLoading}
        confirmLabel="删除"
        onCancel={() => {
          if (agendaLoading) return;
          setDeleting(null);
        }}
        onConfirm={() => void confirmDelete()}
      >
        <p className="text-[11px] leading-relaxed text-slate-400">
          「{deleting?.text ?? ''}」将从清单中删除，无法恢复。
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dismissing !== null}
        title="从议程隐藏这条作业？"
        danger
        busy={agendaLoading}
        confirmLabel="移除"
        onCancel={() => {
          if (agendaLoading) return;
          setDismissing(null);
        }}
        onConfirm={() => void confirmDismiss()}
      >
        <p className="text-[11px] leading-relaxed text-slate-400">
          「{dismissing?.title ?? ''}」只会从议程隐藏；不会修改网络学堂，也不会删除作业本身。
        </p>
        <p className="text-[10px] leading-relaxed text-slate-500">
          作业本身仍保留在网络学堂，之后重新同步也不会自动恢复显示。
        </p>
      </ConfirmDialog>
    </div>
  );
}
